import React, { useEffect, useRef, useState } from "react";
import "./About.css";

const TEAM_MEMBERS_EN = [
  { name: "Annie", role: "Translation & Proofreading" },
  { name: "Cookie", role: "Translation" },
  { name: "Jenny Ping Lam Lin", role: "Research & Editorial" },
  { name: "Max Lee", role: "Artist Entries" },
  { name: "Muki Pan", role: "Artist Entries & UI Design Improvements" },
  { name: "Simon", role: "Research & Editorial" },
  { name: "Whyla", role: "Artist Entries" },
  { name: "Ling", role: "Correspondence & Feedback" },
  { name: "Zane Xiao", role: "Founder, Curation & Development" },
];

const TEAM_MEMBERS_CN = [
  { name: "Annie", role: "翻译与校对" },
  { name: "Cookie", role: "翻译" },
  { name: "練冰藍", role: "研究与编辑" },
  { name: "Max Lee", role: "艺术家条目" },
  { name: "Muki Pan", role: "艺术家条目、UI 设计改进" },
  { name: "Simon", role: "研究与编辑" },
  { name: "Whyla", role: "艺术家条目" },
  { name: "Ling", role: "通讯与反馈" },
  { name: "Zane Xiao", role: "创始人、内容策划与开发" },
];

const SITE_NOTES_EN = [
  {
    date: "July 2026",
    text: "Updated and refined various UI details.",
  },
  {
    date: "May 2026",
    text: "Chinese translation launched; added multiple Asian and women photographer entries.",
  },
  { date: "October 2025", text: "Archive of Light goes live." },
];

const SITE_NOTES_CN = [
  {
    date: "2026 年 7 月",
    text: "部分 UI 细节更新修复。",
  },
  {
    date: "2026 年 5 月",
    text: "上线中文翻译功能；新增多位亚裔摄影师与女性摄影师条目。",
  },
  { date: "2025 年 10 月", text: "初版 Archive of Light 正式上线。" },
];

function About({ language = "EN" }) {
  const [loadingLanguageSwitch, setLoadingLanguageSwitch] = useState(false);
  const isFirstLanguageRender = useRef(true);

  useEffect(() => {
    if (isFirstLanguageRender.current) {
      isFirstLanguageRender.current = false;
      return;
    }
    setLoadingLanguageSwitch(true);
    const timer = setTimeout(() => {
      setLoadingLanguageSwitch(false);
    }, 420);
    return () => clearTimeout(timer);
  }, [language]);

  const teamMembers = language === "CN" ? TEAM_MEMBERS_CN : TEAM_MEMBERS_EN;
  const siteNotes = language === "CN" ? SITE_NOTES_CN : SITE_NOTES_EN;

  return (
    <div className={`about-container ${language === "CN" ? "about-cn" : ""}`}>
      <div className="about-header">
        <h1>
          {language === "CN" ? (
            <>
              <span className="about-title-line">关于</span>
              <span className="about-title-line">ARCHIVE OF LIGHT</span>
            </>
          ) : (
            <>
              <span className="about-title-line">ABOUT</span>
              <span className="about-title-line">ARCHIVE OF LIGHT</span>
            </>
          )}
        </h1>
      </div>

      {loadingLanguageSwitch ? (
        <div className="about-loading-container">
          <div className="about-loading-animation">
            <div className="about-loading-dots">
              <div className="about-dot"></div>
              <div className="about-dot"></div>
              <div className="about-dot"></div>
            </div>
            <p className="about-loading-text">
              {language === "CN" ? "正在加载关于页面..." : "Loading about page..."}
            </p>
          </div>
        </div>
      ) : (
        <div className="about-content">
          <p className="intro-text">
            {language === "CN"
              ? "你好，我是 Zane，欢迎来到光之档案 Archive of Light。"
              : "Hi, I’m Zane—welcome to Archive of Light."}
          </p>

          <p>
            {language === "CN"
              ? "光之档案是互联网一处安静的角落，专注于摄影的历史。你可以把它当作一间可以自由漫步的线上展厅，也可以将它视为深入探索摄影史的起点。"
              : "Archive of Light is a quiet corner of the internet dedicated to the history of photography. You can think of it as an online gallery to wander through, or as a starting point for exploring photography’s past more deeply."}
          </p>

          <p>
            {language === "CN"
              ? "刚开始接触摄影时，我注意到，画家常常会自然地从艺术史中寻找脉络与灵感。我开始思考：我们是否也能以相似的方式走近摄影史，更好地理解自身的创作，并从中获得灵感？"
              : "When I first became interested in photography, I noticed how naturally painters looked to art history for context and inspiration. It made me wonder: could we approach the history of photography in a similar way—using it to better understand our own work and find new inspiration?"}
          </p>

          <p>
            {language === "CN"
              ? "然而，当我开始翻阅书籍、浏览线上档案并参观展览时，却发现相关资料往往过于专业艰深，或散落在不同的书籍、机构与网站中。对于摄影爱好者来说，要从这些庞杂的信息中梳理出摄影史的脉络，或深入了解某位摄影师，往往需要投入大量的时间与精力。"
              : "Yet as I began exploring books, online archives, and exhibitions, I found that the available resources were often highly specialized or scattered across different publications, institutions, and websites. For photography enthusiasts, tracing the broader history of the medium—or learning more about a particular photographer—often requires a great deal of time and effort."}
          </p>

          <p>
            {language === "CN"
              ? "我一直希望能有这样一个空间：它有些像参观一场线上的展览，将摄影史与摄影师的故事以更清晰、易于接近的方式呈现出来。人们不需要具备太多背景知识，也能从自己感兴趣的地方开始，循着线索慢慢探索。"
              : "I kept wishing for a space that felt a little like visiting an exhibition online, where the history of photography and the stories of photographers could be presented in a clearer, more approachable way. A place where no extensive background knowledge was required, and where anyone could begin with something that interested them and slowly follow the threads from there."}
          </p>

          <p>
            {language === "CN"
              ? "后来，我决定亲手建立这个自己一直希望存在的网站。"
              : "Eventually, I decided to build the site I wished already existed."}
          </p>

          <p>
            {language === "CN" ? (
              <>
                光之档案是一个独立、非商业的教育与学习平台。这里的内容均由我们手工搜集、筛选并审核，因此难免仍会有疏漏。如果你发现任何需要更正的地方，或只是想来打声招呼，随时欢迎写信至{" "}
                <a href="mailto:archiveoflightcontact@gmail.com">
                  archiveoflightcontact@gmail.com
                </a>
                。
              </>
            ) : (
              <>
                Archive of Light is an independent, non-commercial platform
                created for education and learning. Everything here is
                researched, selected, and reviewed by hand, so occasional errors
                may remain. If you notice something that needs correcting—or
                simply want to say hello—you’re always welcome to write to{" "}
                <a href="mailto:archiveoflightcontact@gmail.com">
                  archiveoflightcontact@gmail.com
                </a>
                .
              </>
            )}
          </p>

          <h2 className="about-section-title">
            {language === "CN" ? "团队" : "The Team"}
          </h2>
          <p>
            {language === "CN"
              ? "光之档案由一支小型志愿者团队共同建设与维护。"
              : "Archive of Light is built and maintained by a small volunteer team."}
          </p>
          <ul className="about-team-list">
            {teamMembers.map((member) => (
              <li key={member.name}>
                <span className="team-name">{member.name}</span>
                <span className="team-separator">—</span>
                <span className="team-role">{member.role}</span>
              </li>
            ))}
          </ul>
          <p>
            {language === "CN"
              ? "我们因为对摄影共同的热爱而相聚于此。感谢每一位贡献时间与知识的人，让这份档案得以不断丰富和完善。"
              : "We’re brought together by a shared love of photography. Thank you to everyone who contributes their time and knowledge to helping the archive continue to grow."}
          </p>

          <h2 className="about-section-title">
            {language === "CN" ? "网站近况" : "Site Notes"}
          </h2>
          <ul className="about-notes-list">
            {siteNotes.map((note, idx) => (
              <li key={idx}>
                <span className="note-date">{note.date}</span>
                <span className="note-separator">—</span>
                <span className="note-text">{note.text}</span>
              </li>
            ))}
          </ul>

          <p className="copyright-notice">
            <span className="copyright-label">
              {language === "CN" ? "关于版权：" : "On copyright: "}
            </span>
            {language === "CN"
              ? "本站所有图片和材料仅用于教育及非商业用途。如果您认为某项内容侵犯了您的版权，请发送邮件至 "
              : "All images and materials on this site are used for educational, non-commercial purposes. If you believe any content infringes your copyright, please contact us at "}
            <a
              href="mailto:archiveoflightcontact@gmail.com"
              className="copyright-email"
            >
              archiveoflightcontact@gmail.com
            </a>
            {language === "CN"
              ? "，我们会及时删除相关材料。"
              : " and we'll remove it promptly."}
          </p>
        </div>
      )}
    </div>
  );
}

export default About;
